import { Quote } from '@/shared/types';
import { supabaseService } from '@/shared/services/supabaseService';
import { authService } from '@/shared/services/realData/auth';
import { generateShareToken } from '@/shared/utils/shareToken';

/**
 * Quote operations
 * Handles all quote-related database operations
 */
export const quoteService = {
  /**
   * Get all quotes for the current user with related client and items
   */
  async getQuotes(): Promise<Quote[]> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    const { data, error } = await supabaseService.client
      .from('quotes')
      .select(`
        *,
        client:clients(id, name, email, phone, address),
        quote_items(
          id,
          name,
          description,
          quantity,
          unit_price,
          total_price
        )
      `)
      .eq('user_id', supabaseUserId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    if (!data || data.length === 0) return [];

    const { mapDatabaseQuoteToQuote, mapDatabaseClientToClient } = await import('@/shared/services/dataMapper');

    return data.map((dbQuote: any) => {
      const client = dbQuote.client ? mapDatabaseClientToClient(dbQuote.client) : {
        id: '',
        userId: '',
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        notes: ''
      };
      // Map quote items from database schema to QuoteItem format
      const items = (dbQuote.quote_items || []).map((item: any) => ({
        id: item.id,
        serviceId: undefined,
        description: item.description || item.name,
        rate: Number(item.unit_price) || 0,
        quantity: Number(item.quantity) || 1,
        name: item.name
      }));
      return mapDatabaseQuoteToQuote(dbQuote, client, items);
    });
  },

  /**
   * Add a new quote
   */
  async addQuote(quote: Omit<Quote, 'id'>): Promise<void> {
    const supabaseUserId = await authService.getCurrentUserSupabaseId();
    if (!supabaseUserId) throw new Error('User not authenticated or profile not found');

    // Generate quote number if not provided
    let quoteNumber = quote.quoteNumber;
    if (!quoteNumber) {
      // Keep trying to find an available quote number
      let attempts = 0;
      const maxAttempts = 100;

      while (attempts < maxAttempts) {
        // Get ALL quote numbers to find the actual max
        const { data: allQuotes } = await supabaseService.client
          .from('quotes')
          .select('quote_number')
          .eq('user_id', supabaseUserId);

        let maxNum = 0;
        if (allQuotes && allQuotes.length > 0) {
          // Extract all numeric parts and find the max
          const numbers = allQuotes
            .map(q => parseInt(q.quote_number.split('-')[1]))
            .filter(n => !isNaN(n));
          maxNum = Math.max(...numbers, 0);
        }

        quoteNumber = `Q-${String(maxNum + 1 + attempts).padStart(3, '0')}`;

        // Try to insert the quote with this number
        try {
          const { data: quoteData, error: quoteError } = await supabaseService.client
            .from('quotes')
            .insert({
              user_id: supabaseUserId,
              client_id: quote.clientId,
              quote_number: quoteNumber,
              title: quote.title,
              description: quote.description || '',
              status: quote.status || 'draft',
              currency: quote.currency || 'USD',
              subtotal: quote.subtotal || 0,
              tax_rate: quote.taxRate || 0,
              tax_amount: quote.taxAmount || 0,
              total_amount: quote.totalAmount || 0,
              valid_until: quote.validUntil || '',
              notes: quote.notes || '',
              terms: quote.terms || '',
              public_share_token: generateShareToken(),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              // Professional quote fields
              project_name: quote.projectName,
              detail_level: quote.detailLevel,
              intro_text: quote.introText,
              problem_statement: quote.problemStatement,
              proposed_solution: quote.proposedSolution,
              deliverables: quote.deliverables || [],
              timeline: quote.timeline,
              start_date: quote.startDate,
              end_date: quote.endDate,
              payment_schedule: quote.paymentSchedule,
              methodology: quote.methodology,
              team: quote.team || [],
              includes_case_study: quote.includesCaseStudy || false,
              case_study_title: quote.caseStudyTitle,
              case_study_description: quote.caseStudyDescription
            })
            .select()
            .single();

          if (quoteError) throw quoteError;

          // Quote was created successfully, now add items if provided
          if (quote.items && quote.items.length > 0) {
            const quoteItems = quote.items.map((item, index) => ({
              quote_id: quoteData.id,
              name: item.name || item.description || 'Item',
              description: item.description || '',
              quantity: item.quantity,
              unit_price: item.rate,
              order_index: index
            }));

            const { error: itemsError } = await supabaseService.client
              .from('quote_items')
              .insert(quoteItems);

            if (itemsError) throw itemsError;
          }

          // Success! Exit the loop
          return;
        } catch (error: any) {
          // If it's a unique constraint violation on quote_number, try again with next number
          if (error.code === '23505' && error.message.includes('quotes_unique_number')) {
            attempts++;
            continue;
          }
          // Otherwise, it's a different error, throw it
          throw error;
        }
      }

      throw new Error('Could not generate a unique quote number after multiple attempts');
    } else {
      // Quote number was provided, try to insert directly
      const { data: quoteData, error: quoteError } = await supabaseService.client
        .from('quotes')
        .insert({
          user_id: supabaseUserId,
          client_id: quote.clientId,
          quote_number: quoteNumber,
          title: quote.title,
          description: quote.description || '',
          status: quote.status || 'draft',
          currency: quote.currency || 'USD',
          subtotal: quote.subtotal || 0,
          tax_rate: quote.taxRate || 0,
          tax_amount: quote.taxAmount || 0,
          total_amount: quote.totalAmount || 0,
          valid_until: quote.validUntil || '',
          notes: quote.notes || '',
          terms: quote.terms || '',
          public_share_token: generateShareToken(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          // Professional quote fields
          project_name: quote.projectName,
          detail_level: quote.detailLevel,
          intro_text: quote.introText,
          problem_statement: quote.problemStatement,
          proposed_solution: quote.proposedSolution,
          deliverables: quote.deliverables || [],
          timeline: quote.timeline,
          start_date: quote.startDate,
          end_date: quote.endDate,
          payment_schedule: quote.paymentSchedule,
          methodology: quote.methodology,
          team: quote.team || [],
          includes_case_study: quote.includesCaseStudy || false,
          case_study_title: quote.caseStudyTitle,
          case_study_description: quote.caseStudyDescription
        })
        .select()
        .single();

      if (quoteError) throw quoteError;

      // Add quote items if provided
      if (quote.items && quote.items.length > 0) {
        const quoteItems = quote.items.map((item, index) => ({
          quote_id: quoteData.id,
          name: item.name || item.description || 'Item',
          description: item.description || '',
          quantity: item.quantity,
          unit_price: item.rate,
          order_index: index
        }));

        const { error: itemsError } = await supabaseService.client
          .from('quote_items')
          .insert(quoteItems);

        if (itemsError) throw itemsError;
      }
    }
  },

  /**
   * Update an existing quote
   */
  async updateQuote(quote: Quote): Promise<Quote> {
    const { error } = await supabaseService.client
      .from('quotes')
      .update({
        title: quote.title,
        description: quote.description,
        status: quote.status,
        total_amount: quote.totalAmount,
        updated_at: new Date().toISOString()
      })
      .eq('id', quote.id);

    if (error) throw error;
    return quote;
  },

  /**
   * Delete a quote
   */
  async deleteQuote(id: string): Promise<void> {
    const { error } = await supabaseService.client
      .from('quotes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Get a quote by its public share token
   * Used for public quote viewing without authentication
   */
  async getQuoteByPublicToken(token: string): Promise<Quote | null> {
    if (!token) throw new Error('Share token is required');

    const { data, error } = await supabaseService.client
      .from('quotes')
      .select(`
        *,
        client:clients(id, name, email, phone, address, company),
        quote_items(
          id,
          name,
          description,
          quantity,
          unit_price,
          total_price
        )
      `)
      .eq('public_share_token', token)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - token not found
        return null;
      }
      throw error;
    }

    if (!data) return null;

    const { mapDatabaseQuoteToQuote, mapDatabaseClientToClient } = await import('@/shared/services/dataMapper');

    const client = data.client ? mapDatabaseClientToClient(data.client) : {
      id: '',
      userId: '',
      name: '',
      email: '',
      phone: '',
      company: '',
      address: '',
      notes: ''
    };

    const items = (data.quote_items || []).map((item: any) => ({
      id: item.id,
      serviceId: undefined,
      description: item.description || item.name,
      rate: Number(item.unit_price) || 0,
      quantity: Number(item.quantity) || 1,
      name: item.name
    }));

    return mapDatabaseQuoteToQuote(data, client, items);
  },

  /**
   * Accept a quote via public token
   * @param token - Public share token
   * @param message - Optional message from client
   */
  async acceptQuoteByPublicToken(token: string, message?: string): Promise<void> {
    if (!token) throw new Error('Share token is required');

    const { data, error } = await supabaseService.client
      .from('quotes')
      .select('id, status, valid_until, notes')
      .eq('public_share_token', token)
      .single();

    if (error || !data) {
      throw new Error('Quote not found');
    }

    // Check if quote is still valid
    if (data.valid_until && new Date(data.valid_until) < new Date()) {
      throw new Error('This quote has expired');
    }

    // Check if quote can be accepted
    if (data.status === 'accepted' || data.status === 'rejected' || data.status === 'converted') {
      throw new Error('This quote has already been responded to');
    }

    // Update quote status to accepted
    const { error: updateError } = await supabaseService.client
      .from('quotes')
      .update({
        status: 'accepted',
        updated_at: new Date().toISOString(),
        notes: message ? `${data.notes || ''}\n\nClient message: ${message}`.trim() : (data.notes || null)
      })
      .eq('public_share_token', token);

    if (updateError) throw updateError;
  },

  /**
   * Reject a quote via public token
   * @param token - Public share token
   * @param message - Optional message from client
   */
  async rejectQuoteByPublicToken(token: string, message?: string): Promise<void> {
    if (!token) throw new Error('Share token is required');

    const { data, error } = await supabaseService.client
      .from('quotes')
      .select('id, status, valid_until, notes')
      .eq('public_share_token', token)
      .single();

    if (error || !data) {
      throw new Error('Quote not found');
    }

    // Check if quote is still valid
    if (data.valid_until && new Date(data.valid_until) < new Date()) {
      throw new Error('This quote has expired');
    }

    // Check if quote can be rejected
    if (data.status === 'accepted' || data.status === 'rejected' || data.status === 'converted') {
      throw new Error('This quote has already been responded to');
    }

    // Update quote status to rejected
    const { error: updateError } = await supabaseService.client
      .from('quotes')
      .update({
        status: 'rejected',
        updated_at: new Date().toISOString(),
        notes: message ? `${data.notes || ''}\n\nClient rejection message: ${message}`.trim() : data.notes
      })
      .eq('public_share_token', token);

    if (updateError) throw updateError;
  },

  /**
   * Regenerate public share token for a quote
   * Use this if a token is compromised
   */
  async regeneratePublicToken(quoteId: string): Promise<string> {
    const newToken = generateShareToken();

    const { error } = await supabaseService.client
      .from('quotes')
      .update({
        public_share_token: newToken,
        updated_at: new Date().toISOString()
      })
      .eq('id', quoteId);

    if (error) throw error;
    return newToken;
  }
};
